/**
 * AvailabilityBanner — Card composition for the pre-screen page when a
 * candidate is approaching the test's `availability_ends_at`. J-X22 § Where
 * to surface — pre-screen.
 *
 * Renders only when the state is currently_open AND closing within 24h, or
 * when the state is closed (the candidate hit the link past the window).
 * For always_open / scheduled_future, returns null and the caller renders
 * its normal UI.
 *
 * Inv 15: Card from `@/components/ui` barrel.
 */

import { type ReactElement } from 'react';
import { Card, Heading } from '@/components/ui';
import {
  availabilityCopy,
  deriveAvailability,
  formatLocalDateTime,
  relativeCountdown,
  type AvailabilityInput,
  type AvailabilityState,
} from '../../lib/availability';

export interface AvailabilityBannerProps {
  state?: AvailabilityState;
  input?: AvailabilityInput;
  /** Override "now" for tests. */
  now?: Date;
}

const CLOSE_WINDOW_MS = 24 * 3600_000;

export function AvailabilityBanner(
  props: AvailabilityBannerProps,
): ReactElement | null {
  const state =
    props.state ??
    (props.input ? deriveAvailability(props.input, props.now) : null);
  if (state === null) return null;

  const now = props.now ?? new Date();

  if (state.kind === 'closed') {
    const copy = availabilityCopy(state) ?? 'This test is no longer available';
    return (
      <Card variant="default" role="status" className="border-line-2 bg-bg-warm p-4 text-ink">
        <Heading level="h2" className="text-h3 text-ink">
          Test closed
        </Heading>
        <p className="mt-2 font-sans text-body text-ink-2">{copy}</p>
      </Card>
    );
  }

  if (state.kind === 'currently_open' && state.pivot_iso !== null) {
    const ends = new Date(state.pivot_iso).getTime();
    if (ends - now.getTime() <= CLOSE_WINDOW_MS) {
      return (
        <Card variant="default" role="status" className="border-line-2 bg-bg-warm p-4 text-ink">
          <Heading level="h2" className="text-h3 text-ink">
            Closing soon
          </Heading>
          <p className="mt-2 font-sans text-body text-ink-2">
            This test closes {relativeCountdown(state.pivot_iso, now)} at{' '}
            {formatLocalDateTime(state.pivot_iso)}. Finish before then or your
            submission won't count.
          </p>
        </Card>
      );
    }
  }

  return null;
}
